import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch as MuiSwitch,
  Typography,
} from '@mui/material';
import type { PartnershipAdSettingFormValue } from './PartnershipSection';

interface PartnershipAdsToggleProps {
  showLabel?: boolean;
}

const PartnershipAdsToggle: FC<PartnershipAdsToggleProps> = ({
  showLabel = true,
}) => {
  const { control, setValue } = useFormContext();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const partnershipAdSettings = useWatch({
    name: 'partnershipAdSettings',
  }) as PartnershipAdSettingFormValue[] | undefined;

  const hasMultiplePartnershipSections = (partnershipAdSettings?.length ?? 0) > 1;

  return (
    <>
      <Dialog
        open={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
        }}
        PaperProps={{
          sx: { maxWidth: 460, width: '90%' },
        }}
      >
        <DialogTitle>Turn off Partnership ads?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The selected partnerships will be cleared
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size={'small'}
            variant="text"
            color="inherit"
            onClick={() => {
              setIsConfirmOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            size={'small'}
            variant="contained"
            color="error"
            onClick={() => {
              setValue('partnershipAdsEnabled', false, { shouldValidate: true });
              setIsConfirmOpen(false);
            }}
          >
            Turn off
          </Button>
        </DialogActions>
      </Dialog>
      <Controller
        control={control}
        name="partnershipAdsEnabled"
        render={({ field: { value, onChange } }) => {
          const checked = Boolean(value);

          return (
            <FormControlLabel
              sx={{ mx: 0, mb: 0, py: 1 / 2 }}
              control={(
                <MuiSwitch
                  size="medium"
                  checked={checked}
                  onChange={(_, nextChecked) => {
                    if (checked && !nextChecked && hasMultiplePartnershipSections) {
                      setIsConfirmOpen(true);
                      return;
                    }

                    onChange(nextChecked);
                  }}
                  inputProps={{
                    'data-testid': 'partnershipAdsEnabled',
                    'aria-label': 'Partnership ads',
                  }}
                />
              )}
              label={showLabel ? (
                <Typography sx={{ fontSize: 'body2.fontSize', ml: 1 }}>
                  Partnership ads
                </Typography>
              ) : null}
            />
          );
        }}
      />
    </>
  );
};

export default PartnershipAdsToggle;
